import React, { useState, useEffect } from "react";
import { X, Users, Send, Clock, AlertCircle, TrendingUp, Loader2, CheckCircle, ChevronLeft, ChevronRight, Eye, Package } from "lucide-react";
import { useAPI } from "../../Context/MyContext";

const CampaignModal = ({ isOpen, onClose, campaignData, loading, error }) => {
  const { getCampaignMessages } = useAPI();

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const campaignId = campaignData?.campaign?.id || campaignData?.campaign?._id;

  const loadMessages = async (pageNum, limit) => {
    if (!campaignId) return;
    
    try {
      setMessagesLoading(true);
      setMessagesError(null);
      // console.log('📥 Loading messages:', { pageNum, limit, campaignId });
      const data = await getCampaignMessages(campaignId, pageNum, limit);
      // console.log('✅ Messages loaded:', data);
      setMessages(data.messages || []);
      setPagination(data.pagination || {});
    } catch (err) {
      console.error('❌ Error loading messages:', err);
      setMessagesError(err.message || "Failed to load messages");
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  // Load messages when page or items per page changes
  useEffect(() => {
    if (isOpen && campaignId) {
      loadMessages(currentPage, itemsPerPage);
    }
  }, [isOpen, currentPage, itemsPerPage, campaignId]);

  // Reset page when modal opens (don't reset itemsPerPage)
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
    }
  }, [isOpen]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return "N/A";
    }
  };

  const handlePageChange = (newPage) => {
    const totalPages = pagination?.total_pages || 1;
    if (newPage >= 1 && newPage <= totalPages && !messagesLoading) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page when changing limit
  };



  const getStatusBadge = (status, failedReason = "") => {
  const statusConfig = {
    sent: { bg: "bg-blue-100", text: "text-blue-700", label: "Sent", icon: Send },
    delivered: { bg: "bg-green-100", text: "text-green-700", label: "Delivered", icon: CheckCircle },
    read: { bg: "bg-purple-100", text: "text-purple-700", label: "Read", icon: Eye },
    failed: { bg: "bg-red-100", text: "text-red-700", label: "Failed", icon: AlertCircle },
    pending: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Pending", icon: Clock },
  };

  const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
  const IconComponent = config.icon;
  const isFailed = status?.toLowerCase() === "failed";

  return (
    <div className="relative group inline-block">
      {/* Badge */}
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium 
        ${config.bg} ${config.text} 
        ${isFailed ? "cursor-pointer" : "cursor-default"}`}
      >
        <IconComponent className="w-3.5 h-3.5" />
        {config.label}
      </span>

      {/* Tooltip (above the badge) */}
      {isFailed && failedReason && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex 
          bg-gray-900 text-white text-xs rounded-md px-3 py-2 whitespace-normal z-10 w-52 shadow-lg 
          justify-center items-center transition-opacity duration-200 opacity-0 group-hover:opacity-100"
        >
          {failedReason}
          {/* Tooltip Arrow */}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
        </div>
      )}
    </div>
  );
};



  const getCampaignStatusBadge = (status) => {
    const statusConfig = {
      COMPLETED: { bg: "bg-green-500", text: "Completed" },
      IN_PROGRESS: { bg: "bg-blue-500", text: "In Progress" },
      PENDING: { bg: "bg-yellow-500", text: "Pending" },
      FAILED: { bg: "bg-red-500", text: "Failed" },
      DRAFT: { bg: "bg-gray-500", text: "Draft" },
      CANCELLED: { bg: "bg-orange-500", text: "Cancelled" }
    };

    const config = statusConfig[status] || { bg: "bg-gray-500", text: status };

    return (
      <span className={`px-4 py-1.5 rounded-full text-sm font-medium ${config.bg} text-white shadow-sm`}>
        {config.text}
      </span>
    );
  };

  const calculateSuccessRate = () => {
    const statusSummary = campaignData?.status_summary;
    if (!statusSummary) return 0;

    const total = statusSummary.total || 0;
    const successful = (statusSummary.sent || 0) + (statusSummary.delivered || 0) + (statusSummary.read || 0);
    if (total === 0) return 0;
    return ((successful / total) * 100).toFixed(1);
  };

  if (!isOpen) return null;

  const campaign = campaignData?.campaign || campaignData?.compaign;
  const statusSummary = campaignData?.status_summary || {};
  const totalContacts = statusSummary.total || campaign?.total_contacts || 0;
  const sentCount = statusSummary.sent || 0;
  const deliveredCount = statusSummary.delivered || 0;
  const readCount = statusSummary.read || 0;
  const failedCount = statusSummary.failed || 0;
  const pendingCount = statusSummary.pending || 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl relative max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white shadow-sm">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Campaign Analytics</h2>
              <p className="text-sm text-gray-600 mt-0.5">Comprehensive campaign performance overview</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/80 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6">
          {loading ? (
            <div className="flex flex-col justify-center items-center gap-3 py-20">
              <Loader2 className="animate-spin w-10 h-10 text-blue-600" />
              <span className="text-gray-600 font-medium">Loading campaign details...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col justify-center items-center gap-3 py-20">
              <AlertCircle className="w-12 h-12 text-red-500" />
              <p className="text-red-600 font-medium">{error}</p>
            </div>
          ) : campaign ? (
            <div className="space-y-6">

              {/* Campaign Header */}
              <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {campaign?.campaign_name || campaign?.name || "Unnamed Campaign"}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                      <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg shadow-sm">
                        <Package className="w-4 h-4 text-indigo-600" />
                        <span className="font-medium">Template:</span>
                        <span className="font-semibold text-gray-800">{campaign?.template_name || "N/A"}</span>
                      </div>
                      {campaign?.language && (
                        <div className="bg-white px-3 py-1.5 rounded-lg shadow-sm">
                          <span className="font-medium">{campaign.language}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {campaign?.status && getCampaignStatusBadge(campaign.status)}
                </div>
              </div>

              {/* Main Stats - Total Contacts Card */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-700 rounded-xl p-6 shadow-lg border border-gray-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-300 mb-1">Total Contacts</p>
                      {/* <p className="text-5xl font-bold text-white">{sentCount + failedCount}</p> */}
                      <p className="text-5xl font-bold text-white">{totalContacts}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-300 mb-1">Campaign Reach</p>
                    <p className="text-2xl font-semibold text-white">{sentCount} Recipients</p>
                  </div>
                </div>
              </div>

              {/* Message Status Grid - 3 Columns */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Sent Status */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border-2 border-blue-200 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-3 bg-blue-500 rounded-xl shadow-md">
                      <Send className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Sent</p>
                      <p className="text-3xl font-bold text-blue-700">{sentCount}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-blue-200">
                    <span className="text-xs text-blue-600 font-medium">Messages Sent</span>
                    <span className="text-sm font-bold text-blue-700">{totalContacts > 0 ? ((sentCount / totalContacts) * 100).toFixed(1) : 0}%</span>
                  </div>
                </div>

                {/* Delivered Status */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border-2 border-green-200 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-3 bg-green-500 rounded-xl shadow-md">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-semibold text-green-600 uppercase tracking-wide">Delivered</p>
                      <p className="text-3xl font-bold text-green-700">{deliveredCount}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-green-200">
                    <span className="text-xs text-green-600 font-medium">Successfully Delivered</span>
                    <span className="text-sm font-bold text-green-700">{totalContacts > 0 ? ((deliveredCount / totalContacts) * 100).toFixed(1) : 0}%</span>
                  </div>
                </div>

                {/* Read Status */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 border-2 border-purple-200 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-3 bg-purple-500 rounded-xl shadow-md">
                      <Eye className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Read</p>
                      <p className="text-3xl font-bold text-purple-700">{readCount}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-purple-200">
                    <span className="text-xs text-purple-600 font-medium">Messages Read</span>
                    <span className="text-sm font-bold text-purple-700">{totalContacts > 0 ? ((readCount / totalContacts) * 100).toFixed(1) : 0}%</span>
                  </div>
                </div>
              </div>

              {/* Additional Stats - Failed & Pending */}
              {(failedCount > 0 || pendingCount > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Failed */}
                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-5 border-2 border-red-200 shadow-md hover:shadow-lg transition-all">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-3 bg-red-500 rounded-xl shadow-md">
                        <AlertCircle className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-semibold text-red-600 uppercase tracking-wide">Failed</p>
                        <p className="text-3xl font-bold text-red-700">{failedCount}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-red-200">
                      <span className="text-xs text-red-600 font-medium">Delivery Failed</span>
                      <span className="text-sm font-bold text-red-700">{totalContacts > 0 ? ((failedCount / totalContacts) * 100).toFixed(1) : 0}%</span>
                    </div>
                  </div>

                  {/* Pending */}
                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-5 border-2 border-yellow-200 shadow-md hover:shadow-lg transition-all">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-3 bg-yellow-500 rounded-xl shadow-md">
                        <Clock className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-semibold text-yellow-600 uppercase tracking-wide">Pending</p>
                        <p className="text-3xl font-bold text-yellow-700">{pendingCount}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-yellow-200">
                      <span className="text-xs text-yellow-600 font-medium">In Queue</span>
                      <span className="text-sm font-bold text-yellow-700">{totalContacts > 0 ? ((pendingCount / totalContacts) * 100).toFixed(1) : 0}%</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Timeline */}
              {campaign && (
                <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-gray-700" />
                    <h4 className="text-lg font-semibold text-gray-900">Campaign Timeline</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1 font-semibold uppercase">Created</p>
                      <p className="font-semibold text-gray-900">{formatDate(campaign.createdAt)}</p>
                    </div>
                    {campaign.started_at && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-xs text-blue-600 mb-1 font-semibold uppercase">Started</p>
                        <p className="font-semibold text-gray-900">{formatDate(campaign.started_at)}</p>
                      </div>
                    )}
                    {campaign.completed_at && (
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-xs text-green-600 mb-1 font-semibold uppercase">Completed</p>
                        <p className="font-semibold text-gray-900">{formatDate(campaign.completed_at)}</p>
                      </div>
                    )}
                    {campaign.updatedAt && !campaign.completed_at && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1 font-semibold uppercase">Last Updated</p>
                        <p className="font-semibold text-gray-900">{formatDate(campaign.updatedAt)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Messages Table */}
              {messages && messages.length > 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
                  <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Send className="w-5 h-5 text-gray-700" />
                        <h4 className="text-lg font-semibold text-gray-900">
                          Message Details
                        </h4>
                      </div>
                      <span className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold shadow-sm">
                        {pagination?.total_records || messages.length} Messages
                      </span>
                    </div>
                  </div>

                  {messagesLoading ? (
                    <div className="flex justify-center items-center py-12">
                      <Loader2 className="animate-spin w-8 h-8 text-blue-600" />
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-100 border-b-2 border-gray-200">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Recipient
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Phone Number
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Sent At
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {messages.map((msg, index) => (
                              <tr key={msg._id || msg.message_id || index} className="hover:bg-blue-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="font-semibold text-gray-900">{msg.recipient_name || "N/A"}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-gray-700 font-mono text-sm bg-gray-100 px-2 py-1 rounded">{msg.recipient_phone}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {getStatusBadge(msg.status, msg.error)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm text-gray-600">{formatDate(msg.sent_at || msg.createdAt)}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Advanced Pagination Controls */}
                      {pagination && pagination.total_pages > 1 && (
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                            {/* Left: Items per page dropdown */}
                            <div className="flex items-center gap-3">
                              <label htmlFor="itemsPerPage" className="text-sm font-medium text-gray-700">
                                Show per page:
                              </label>
                              <select
                                id="itemsPerPage"
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                                disabled={messagesLoading}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                              >
                                <option value={10}>10 per page</option>
                                <option value={20}>20 per page</option>
                                <option value={50}>50 per page</option>
                              </select>
                            </div>

                            {/* Center: Page info */}
                            <div className="text-sm text-gray-600 font-medium">
                              Page <span className="font-bold text-gray-900">{currentPage}</span> of <span className="font-bold text-gray-900">{pagination.total_pages}</span>
                              <span className="ml-3 text-gray-500">
                                ({pagination.total_records} total messages)
                              </span>
                            </div>

                            {/* Right: Pagination buttons */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || messagesLoading}
                                className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-indigo-500 hover:text-white hover:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-700 disabled:hover:border-gray-300 transition-all"
                                title="Previous page"
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </button>

                              <div className="flex items-center gap-1">
                                {[...Array(pagination.total_pages)].map((_, idx) => {
                                  const pageNum = idx + 1;
                                  const showPage =
                                    pageNum === 1 ||
                                    pageNum === pagination.total_pages ||
                                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);

                                  if (!showPage) {
                                    if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                      return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                                    }
                                    return null;
                                  }

                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => handlePageChange(pageNum)}
                                      disabled={messagesLoading}
                                      className={`min-w-[36px] h-9 px-3 rounded-lg text-sm font-semibold transition-all ${currentPage === pageNum
                                        ? "bg-indigo-600 text-white border-2 border-indigo-600 shadow-md"
                                        : "border border-gray-300 text-gray-700 hover:bg-indigo-500 hover:text-white hover:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        }`}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}
                              </div>

                              <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === pagination.total_pages || messagesLoading}
                                className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-indigo-500 hover:text-white hover:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-700 disabled:hover:border-gray-300 transition-all"
                                title="Next page"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 shadow-sm p-16">
                  <div className="flex flex-col items-center justify-center gap-4">
                    <div className="p-4 bg-gray-100 rounded-full">
                      <Send className="w-12 h-12 text-gray-400" />
                    </div>
                    <p className="text-gray-600 font-semibold text-lg">No messages found</p>
                    <p className="text-sm text-gray-500">Messages will appear here once the campaign starts sending</p>
                  </div>
                </div>
              )}

            </div>
          ) : (
            <div className="flex flex-col justify-center items-center gap-3 py-20">
              <AlertCircle className="w-12 h-12 text-gray-400" />
              <p className="text-gray-600 font-medium">No campaign data available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && campaign && (
          <div className="p-5 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 flex justify-end">
            <button
              onClick={onClose}
              className="px-8 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignModal;